/**
 * Information panel (desktop side panel / mobile bottom sheet):
 * address header, coordinates (WGS84 + VN-2000), tabs for planning levels, project documents,
 * nearby facilities and parcel analysis results.
 */
import { $, $$, esc, icon, fmt, toast, copyText, isMobile, hideAnimated, reducedMotion, downloadText, lazyImport } from './ui.js';
import { api, getJSON, isAbort } from './api.js';
import { bus, writeHash, shareUrl, prefs } from './state.js';
import { GT } from './transport.js';
import { compute } from './compute.js';
import { roadTab, roadLoading, roadError, roadOverlay, roadTag, readout, stationAt } from './roadtab.js';
import { LEVEL_META, NEARBY_GROUPS, ESRI_GEOCODE, PHOTON, HANOI_BBOX, PLAN_BY_NO, planFrom } from './config.js';
import { toVN2000, formatVN } from './core/vn2000.js';
import { distanceToGeometryEdges, coordsBBox, interiorPoint } from './core/geo.js';
import { fold } from './core/text.js';
import { SDD, sddCatalog, loadSddCatalog, sddPeriod, sddLayersAt } from './sddlayer.js';
import { amendResult, loadCatalog, catalogDoc } from './doan.js';
import { val, attrOf, attrRows, levelLabel, placeInfo } from './summary.js';
import { findSaved, addSaved, removeSaved, restoreSaved, updateSaved } from './savedstore.js';
import { act, area } from './telemetry.js';
import { giaTab, loadGiaFor, giaClick, giaTeaser, giaLayerOn } from './gia.js';

let pm = null;
let seq = 0;
let sel = null;          // current selection
let analysis = null;     // { result, ring, zone, layerNo }
const els = {};

/* ======================================================================
   public API
   ====================================================================== */

export function initPanel(planMap) {
  pm = planMap;
  els.panel = $('#panel');
  els.head = $('#panel-head');
  els.tabs = $('#panel-tabs');
  els.body = $('#panel-body');
  els.scroll = $('#panel-scroll');
  els.panel.addEventListener('click', onClick);
  els.panel.addEventListener('keydown', (e) => {
    const h = e.target.closest('.card-head[tabindex]');
    if (h && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggleCard(h.closest('.card')); }
    if (e.target.closest('[role=tab]') && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
      const tabs = $$('[role=tab]', els.tabs);
      const i = tabs.indexOf(e.target.closest('[role=tab]'));
      const n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
      n.focus(); setTab(n.dataset.tab);
    }
  });
  els.panel.addEventListener('pointerover', onHoverRow);
  els.panel.addEventListener('pointerout', onHoverRow);
  els.panel.addEventListener('pointerover', onHoverSnap);
  els.panel.addEventListener('pointerout', onHoverSnap);
  els.panel.addEventListener('pointermove', onRoadChart);
  els.panel.addEventListener('pointerleave', onRoadChart, true);
  els.panel.addEventListener('pointerover', onHoverBld);
  els.panel.addEventListener('pointerout', onHoverBld);
  // switching the map to the transport view while a point is open shows its road tab
  bus.on('plan', (p) => { if (p === GT && sel && sel.tab === 'plan' && sel.kind !== 'parcel') setTab('road'); });
  bus.on('saved', syncSaveButton);
  initSheet();
}

export const currentSelection = () => sel;
export const isOpen = () => !els.panel.hidden;

/** Select a location: show pin, open panel, query all planning levels in parallel. */
export function openAt({ lat, lng }, opts = {}) {
  sel?.ctl.abort();
  const s = {
    id: ++seq, lat, lng,
    label: opts.label || null, sub: opts.sub || null, kind: opts.kind || 'point',
    ctl: new AbortController(), result: null, error: null, qh100: undefined, nearby: undefined, nearest: undefined,
    radius: sel?.radius || 500, address: opts.label ? { title: opts.label, sub: opts.sub || '' } : null,
    tab: opts.tab || (sel && opts.keepTab ? sel.tab : prefs.plan === GT ? 'road' : giaLayerOn() ? 'gia' : 'plan'),
    road: undefined,
    gia: undefined,
    zoomAtPick: pm.map.getZoom(),
    snapped: !!opts.snapped,
    projectGeom: opts.project?.geometry || null,
    focus: opts.focusProject || null, // project whose card opens first in the documents tab
    t0: performance.now(),
  };
  sel = s;
  act('pick:' + s.kind, area(lat, lng));
  if (!opts.parcel && s.kind !== 'gps') { pm.setPin({ lat, lng }); } else if (s.kind === 'gps') pm.setPin(null);
  pm.highlight(null, s.projectGeom);
  pm.setPois([]);
  clearSection();
  clearRoad();
  show();
  renderAll();
  if (s.tab === 'road') loadRoad(s);
  if (!opts.parcel) loadGia(s);
  if (opts.fly !== false) pm.goTo({ lat, lng }, { zoom: opts.zoom, padding: viewPadding() });
  writeHash({ pt: { lat, lng } });

  const signal = s.ctl.signal;
  api.point(lat, lng, { signal }).then((r) => {
    if (s !== sel) return;
    s.result = r;
    s.ms = performance.now() - s.t0;
    // corrected codes / scales and the decision's figures from the project catalogue
    if (!amendResult(r) && !catalogDoc()) {
      loadCatalog().then(() => { if (s === sel && amendResult(r)) { renderTabs(); if (s.tab === 'plan' || s.tab === 'docs') renderBody(); } }).catch(() => {});
    }
    if (!opts.parcel && !r.levels.some((l) => l.cells.length) && inHanoi(lat, lng)) { loadSection(s); loadNearest(s); }
    applyHighlight();
    renderTabs();
    if (s.tab === 'plan' || s.tab === 'docs') renderBody();
    bus.emit('selection', s);
    refreshSaved(s);
  }).catch((e) => {
    if (isAbort(e) || s !== sel) return;
    s.error = e;
    if (s.tab === 'plan' || s.tab === 'docs') renderBody();
  });
  api.qh100(lat, lng, { signal, track: false }).then((r) => {
    if (s !== sel) return;
    s.qh100 = r;
    patchQh100();
    bus.emit('selection', s);
  }).catch((e) => {
    if (isAbort(e) || s !== sel) return;
    s.qh100 = { error: e.message || 'Lỗi tải dữ liệu' };
    patchQh100();
  });
  if (!s.address) {
    reverseGeocode(lat, lng, signal).then((a) => {
      if (s !== sel || !a) return;
      s.address = a;
      renderHead();
      refreshSaved(s);
    });
  }
  // nearby facilities: prefetch quietly (shown in its own tab)
  setTimeout(() => { if (s === sel && s.nearby === undefined) loadNearby(s); }, s.tab === 'near' ? 0 : 700);
}

export async function closePanel() {
  sel?.ctl.abort();
  sel = null;
  bus.emit('selection', null);
  pm.setPin(null);
  pm.highlight(null, null);
  pm.setPois([]);
  clearSection();
  clearRoad();
  writeHash({ pt: null });
  if (!(await hideAnimated(els.panel, 'closing', 220))) return; // re-opened meanwhile
  document.body.classList.remove('has-panel');
  bus.emit('panel', false);
}

/** Show a parcel analysis result (opens the "Khu đất" tab). */
export function showAnalysis(result, ring, zone) {
  const best = [673, 654, 666, 895, 667].find((n) => result.layers.find((l) => l.layerNo === n && l.covered > 0));
  analysis = { result, ring, zone, layerNo: best || result.layers[0]?.layerNo, focus: null };
  const c = interiorOf(ring);
  openAt({ lat: c[1], lng: c[0] }, { tab: 'parcel', fly: false, parcel: true, kind: 'parcel' });
  drawPieces();
  pm.fitBBox(result.parcel.bbox, { padding: viewPadding() });
}

export function clearAnalysis() {
  analysis = null;
  pm.setData('pieces', null);
  if (sel?.tab === 'parcel') renderBody();
  renderTabs();
}

export const hasAnalysis = () => !!analysis;

/** Map padding that keeps the selected point visible beside the panel / above the sheet. */
export function viewPadding() {
  const open = els.panel && !els.panel.hidden;
  if (isMobile()) {
    const sheet = open ? Math.min(els.panel.getBoundingClientRect().height || innerHeight * 0.46, innerHeight * 0.62) : 0;
    return { top: 64, bottom: sheet, left: 0, right: 0 };
  }
  const left = open ? Math.round($('#left-col').getBoundingClientRect().right) : 0;
  return { top: 0, bottom: 0, left, right: 64 };
}

/* ======================================================================
   rendering
   ====================================================================== */

function show() {
  els.panel.classList.remove('closing'); // cancels a running close animation
  if (els.panel.hidden) {
    els.panel.hidden = false;
    document.body.classList.add('has-panel');
    if (isMobile()) setSheet(sheetSnaps()[1], false);
    bus.emit('panel', true);
  }
}

function renderAll() { renderHead(); renderTabs(); renderBody(); els.scroll.scrollTop = 0; }

/** Inject a stagger index into the first tag of each block. */
// stagger the entrance; merge into an existing style attribute (a second one would be dropped, losing e.g. --sw)
const stagger = (parts) => parts.filter(Boolean).map((h, i) => (/^\s*<[a-z0-9]+[^>]*?\sstyle="/i.test(h)
  ? h.replace(/^(\s*<[a-z0-9]+[^>]*?\sstyle=")/i, `$1--i:${i};`)
  : h.replace(/^\s*<([a-z0-9]+)/i, `<$1 style="--i:${i}"`))).join('');

function vnOf(s) { return toVN2000(s.lat, s.lng, 'hn-105'); }

function renderHead() {
  const s = sel;
  if (!s) return;
  const vn = vnOf(s);
  const a = s.address;
  const isParcel = s.kind === 'parcel' && analysis;
  const eyebrow = isParcel ? `${icon('land-plot')} Khu đất đã chọn` : s.kind === 'search' ? `${icon('search')} Kết quả tìm kiếm` : s.kind === 'gps' ? `${icon('locate-fixed')} Vị trí của bạn` : s.kind === 'saved' ? `${icon('star')} Địa điểm đã lưu` : `${icon('map-pin')} Vị trí đã chọn`;
  const title = isParcel ? `Khu đất ${fmt.m2(analysis.result.parcel.area)}` : a ? a.title : null;
  const sub = isParcel ? (a ? `Gần ${a.title}${a.sub ? ', ' + a.sub : ''}` : 'Đang xác định địa chỉ…') : a ? a.sub : 'Đang xác định địa chỉ…';
  const q = `${s.lat.toFixed(6)},${s.lng.toFixed(6)}`;
  els.head.innerHTML = `
    <div class="head-row">
      <span class="eyebrow">${eyebrow}</span>
      <div class="head-actions">
        ${saveButton(s)}
        <button type="button" class="icon-btn ghost sm" data-act="share" aria-label="Chia sẻ vị trí này" aria-haspopup="dialog" data-tip="Chia sẻ">${icon('share-2')}</button>
        <button type="button" class="icon-btn ghost sm" data-act="close" aria-label="Đóng" data-tip="Đóng  (Esc)">${icon('x')}</button>
      </div>
    </div>
    <h2 class="addr-title">${title ? esc(title) : '<span class="sk sk-title" style="display:block"></span>'}</h2>
    <p class="addr-sub">${esc(sub || '')}</p>
    <div class="coords">
      <button type="button" class="coord" data-act="copy-wgs" aria-label="Sao chép toạ độ WGS84">
        <span class="k">WGS84<span class="k-x">· vĩ độ, kinh độ</span> ${icon('copy')}</span>
        <span class="v two"><span>${s.lat.toFixed(6)}</span><span>${s.lng.toFixed(6)}</span></span>
      </button>
      <button type="button" class="coord" data-act="copy-vn" aria-label="Sao chép toạ độ VN-2000">
        <span class="k">VN-2000 · KTT 105°00′ ${icon('copy')}</span>
        <span class="v two"><span>X ${formatVN(vn.x, 2)}</span><span>Y ${formatVN(vn.y, 2)}</span></span>
      </button>
    </div>
    <div class="quick-actions">
      <a class="qa" href="https://www.google.com/maps/dir/?api=1&destination=${q}" target="_blank" rel="noopener" data-tip="Chỉ đường tới đây trên Google Maps"><span class="qa-ico">${icon('navigation')}</span><span class="qa-t">Chỉ đường</span></a>
      <a class="qa" href="https://www.google.com/maps/search/?api=1&query=${q}" target="_blank" rel="noopener" data-tip="Mở vị trí trên Google Maps"><span class="qa-ico">${icon('external-link')}</span><span class="qa-t">Google Maps</span></a>
      <button type="button" class="qa" data-act="copy-summary" aria-label="Sao chép thông tin quy hoạch" data-tip="Sao chép thông tin quy hoạch"><span class="qa-ico">${icon('clipboard-copy')}</span><span class="qa-t">Sao chép</span></button>
      <button type="button" class="qa" data-act="sheet" aria-label="Tạo phiếu thông tin quy hoạch (PDF)" aria-haspopup="dialog" data-tip="Phiếu thông tin quy hoạch (PDF)"><span class="qa-ico">${icon('file-down')}</span><span class="qa-t">Phiếu PDF</span></button>
    </div>`;
}

function saveButton(s) {
  const on = !!findSaved(s.lat, s.lng);
  return `<button type="button" class="icon-btn ghost sm save-btn${on ? ' on' : ''}" data-act="save" aria-pressed="${on}" aria-label="${on ? 'Bỏ lưu địa điểm này' : 'Lưu địa điểm này'}" data-tip="${on ? 'Đã lưu · nhấn để bỏ lưu' : 'Lưu địa điểm'}">${icon('star')}</button>`;
}

function syncSaveButton() {
  const b = sel && els.head.querySelector('[data-act="save"]');
  if (b) b.outerHTML = saveButton(sel);
}

/** Record of the current selection for the saved-places list. */
function savedRecord(s) {
  const info = placeInfo(s);
  const a = s.address;
  const p = info.primary;
  const name = s.kind === 'parcel' && analysis ? `Khu đất ${fmt.m2(analysis.result.parcel.area)}` : a?.title || `${s.lat.toFixed(5)}, ${s.lng.toFixed(5)}`;
  return {
    name, lat: s.lat, lng: s.lng,
    addr: a ? [a.title !== name ? a.title : '', a.sub].filter(Boolean).join(', ') : '',
    plan: p ? { fn: p.fn, code: p.code, color: p.color, level: p.label } : null,
  };
}

function toggleSaved(s) {
  const cur = findSaved(s.lat, s.lng);
  if (cur) {
    const undo = removeSaved(cur.id);
    act('save', 'remove');
    toast(`Đã bỏ lưu “${cur.name}”`, { type: 'info', ms: 5000, action: { label: 'Hoàn tác', onClick: () => undo && restoreSaved(undo) } });
    return;
  }
  const rec = addSaved(savedRecord(s));
  if (!rec) return;
  act('save', 'add');
  toast(`Đã lưu “${rec.name}”`, { type: 'ok', ms: 5000, actions: [
    { label: 'Hoàn tác', onClick: () => removeSaved(rec.id) },
    { label: 'Xem danh sách', onClick: () => bus.emit('saved-open', { id: rec.id }) },
  ] });
}

/** Keep a saved place's summary current: plan data loaded later, address found after saving. */
function refreshSaved(s) {
  const cur = findSaved(s.lat, s.lng);
  if (!cur) return;
  const patch = {};
  if (s.result) {
    const p = placeInfo(s).primary;
    const plan = p ? { fn: p.fn, code: p.code, color: p.color, level: p.label } : null;
    if (JSON.stringify(plan) !== JSON.stringify(cur.plan)) patch.plan = plan;
  }
  if (s.address && /^-?\d+\.\d+, -?\d+\.\d+$/.test(cur.name)) {
    const r = savedRecord(s);
    patch.name = r.name; patch.addr = r.addr;
  }
  if (Object.keys(patch).length) updateSaved(cur.id, patch);
}

function tabDefs() {
  const s = sel;
  const docsN = s?.result ? s.result.levels.reduce((n, l) => n + (l.project ? 1 : 0), 0) : null;
  return [
    { id: 'plan', label: 'Quy hoạch' },
    { id: 'gia', label: 'Giá đất' },
    { id: 'road', label: 'Giao thông' },
    { id: 'docs', label: 'Đồ án', count: docsN },
    { id: 'near', label: 'Lân cận' },
    { id: 'parcel', label: 'Khu đất', count: analysis ? '•' : null },
  ];
}

function renderTabs() {
  if (!sel) return;
  els.tabs.innerHTML = tabDefs().map((t) => `
    <button type="button" class="tab" role="tab" id="tab-${t.id}" data-tab="${t.id}" aria-selected="${sel.tab === t.id}" aria-controls="panel-body" tabindex="${sel.tab === t.id ? 0 : -1}">
      ${esc(t.label)}${t.count != null && t.count !== 0 ? `<span class="count">${t.count}</span>` : ''}
    </button>`).join('');
}

function setTab(id) {
  if (!sel || sel.tab === id) return;
  const prev = sel.tab;
  sel.tab = id;
  act('tab', id);
  for (const b of $$('[role=tab]', els.tabs)) { const on = b.dataset.tab === id; b.setAttribute('aria-selected', on); b.tabIndex = on ? 0 : -1; }
  renderBody();
  els.scroll.scrollTo({ top: Math.min(els.scroll.scrollTop, els.head.offsetHeight), behavior: reducedMotion() ? 'auto' : 'smooth' });
  if (id === 'near') { if (sel.nearby === undefined) loadNearby(sel); else showPoiMarkers(); }
  else pm.setPois([]);
  if (id === 'gia' && sel.gia === undefined) loadGia(sel);
  if (id !== 'gia' && prev === 'gia') applyHighlight();
  if (id === 'road') { pm.setData('section', null); pm.setTag(null); if (sel.road === undefined) loadRoad(sel); else drawRoad(sel); }
  else { clearRoad(); if (isRoad(sel.section)) drawSection(sel); }
  if (analysis) drawPieces();
}

function renderBody() {
  const s = sel;
  if (!s) return;
  els.body.setAttribute('aria-labelledby', 'tab-' + s.tab);
  let html = '';
  if (s.tab === 'plan') html = planTab(s);
  else if (s.tab === 'docs') html = docsTab(s);
  else if (s.tab === 'near') html = nearTab(s);
  else if (s.tab === 'road') html = roadBody(s);
  else if (s.tab === 'gia') html = stagger(giaTab(s));
  else html = parcelTab();
  els.body.innerHTML = html;
}

/* ---------------- tab: planning ---------------- */

function skeleton() {
  return stagger([
    '<div class="hero"><div class="hero-top"><span class="sk" style="width:40px;height:40px;border-radius:11px;flex:none"></span><div style="flex:1"><div class="sk sk-title"></div><div class="sk sk-line" style="width:55%"></div></div></div><div class="metrics"><div class="sk sk-block" style="height:52px"></div><div class="sk sk-block" style="height:52px"></div><div class="sk sk-block" style="height:52px"></div></div></div>',
    '<div class="sk sk-block" style="height:58px"></div>',
    '<div class="sk sk-block" style="height:58px"></div>',
    '<div class="sk sk-block" style="height:58px"></div>',
  ]);
}

function errorBlock(msg) {
  return `<div class="note warn">${icon('triangle-alert')}<div><b>Không tải được dữ liệu quy hoạch.</b><br>${esc(msg || '')}<div style="margin-top:8px"><button type="button" class="btn sm" data-act="retry">${icon('rotate-ccw')}Thử lại</button></div></div></div>`;
}

const kv = (rows) => rows.length ? `<div class="kv">${rows.map(([k, v]) => `<div>${esc(k)}</div><div>${esc(v)}</div>`).join('')}</div>` : '';

function heroMetrics(cell) {
  const out = [];
  const area = attrOf(cell, 'dienTich_m2') || attrOf(cell, 'Dtich_m2');
  const areaHa = attrOf(cell, 'dienTich_ha');
  if (area) out.push(['Diện tích ô đất', fmt.area(+area.value, { html: true })]);
  else if (areaHa) out.push(['Diện tích ô đất', `${fmt.num(areaHa.value, 2)}<small>ha</small>`]);
  else if (Number.isFinite(cell.areaComputed)) out.push(['Diện tích (tính toán)', fmt.area(cell.areaComputed, { html: true })]);
  const tMin = attrOf(cell, 'tangCaoMin'), tMax = attrOf(cell, 'tangCaoMax');
  if (tMin || tMax) out.push(['Tầng cao', `${tMin && tMax && val(tMin) !== val(tMax) ? esc(val(tMin)) + '–' + esc(val(tMax)) : esc(val(tMax || tMin))}<small>tầng</small>`]);
  const dMax = attrOf(cell, 'matDoXayDungMax') || attrOf(cell, 'matDoXayDungMin');
  if (dMax) out.push(['Mật độ xây dựng', `${dMax.key.endsWith('Max') ? '≤ ' : '≥ '}${esc(val(dMax))}<small>%</small>`]);
  const hs = attrOf(cell, 'heSoSDD');
  if (hs) out.push(['Hệ số sử dụng đất', `${esc(val(hs))}<small>lần</small>`]);
  const ds = attrOf(cell, 'danSo') || attrOf(cell, 'quyMoDanSo');
  if (ds && out.length < 4) out.push(['Dân số', `${esc(val(ds))}<small>người</small>`]);
  return out.slice(0, 4);
}

function hero(level) {
  const cell = level.cells[0];
  const p = level.project;
  const title = cell.function || cell.category || cell.name || 'Ô đất quy hoạch';
  const badges = [];
  if (cell.code) badges.push(`<span class="badge code" data-tip="Ký hiệu ô đất">${icon('hash')}${esc(cell.code)}</span>`);
  if (cell.landType && fold(cell.landType) !== fold(title)) badges.push(`<span class="badge">${esc(cell.landType)}</span>`);
  const st = attrOf(cell, 'trangThaiQuyHoach');
  if (st) badges.push(`<span class="badge ${/hiện trạng/i.test(st.value) ? '' : 'accent'}">${esc(st.value)}</span>`);
  if (p?.status) badges.push(`<span class="badge ${/phê duyệt/i.test(p.status) ? 'ok' : 'warn'}">${icon(/phê duyệt/i.test(p.status) ? 'badge-check' : 'clock')}${esc(p.status)}</span>`);
  const m = heroMetrics(cell);
  return `<section class="hero" style="--sw:${esc(cell.color?.hex || '#94a3b8')}">
    <div class="hero-top">
      <span class="swatch lg" style="--sw:${esc(cell.color?.hex || '#94a3b8')}"></span>
      <div style="min-width:0">
        <div class="hero-title">${esc(title)}</div>
        <div class="hero-sub">${esc(levelLabel(level))}</div>
        ${badges.length ? `<div class="badges">${badges.join('')}</div>` : ''}
      </div>
    </div>
    ${m.length ? `<div class="metrics">${m.map(([k, v]) => `<div class="metric"><div class="k">${esc(k)}</div><div class="v">${v}</div></div>`).join('')}</div>` : ''}
  </section>`;
}

const inHanoi = (lat, lng) => lng >= HANOI_BBOX[0] && lng <= HANOI_BBOX[2] && lat >= HANOI_BBOX[1] && lat <= HANOI_BBOX[3];

/** Closest cells around a pick that fell on a road / gap (geocoded street addresses often do). */
function loadNearest(s) {
  s.nearest = null; // loading
  api.nearest(s.lat, s.lng, { signal: s.ctl.signal }).then((r) => {
    if (s !== sel) return;
    s.nearest = r;
    patchHero();
  }).catch((e) => {
    if (isAbort(e) || s !== sel) return;
    s.nearest = { items: [] };
    patchHero();
  });
}

/**
 * A pick inside an approved project but outside every land-use cell is, in the zone / detail
 * plans, almost always the road reserve (the plans leave roads as the space between the
 * red-line boundaries of the cells). Estimate the right-of-way across that gap.
 */
function loadSection(s) {
  const lv = s.result?.levels || [];
  const level = lv.find((l) => l.key === 'detail' && l.covered) || lv.find((l) => l.key === 'zone' && l.covered);
  if (!level) return;
  s.section = null; // loading
  s.sectionLevel = level;
  api.section(s.lat, s.lng, level.key === 'detail' ? 673 : 654, { signal: s.ctl.signal }).then((r) => {
    if (s !== sel) return;
    s.section = r;
    drawSection(s);
    patchHero();
  }).catch((e) => {
    if (isAbort(e) || s !== sel) return;
    s.section = { error: true };
    patchHero();
  });
}

const COMPASS8 = ['Bắc', 'Đông Bắc', 'Đông', 'Đông Nam', 'Nam', 'Tây Nam', 'Tây', 'Tây Bắc'];
const AXIS4 = ['Bắc – Nam', 'Đông Bắc – Tây Nam', 'Đông – Tây', 'Tây Bắc – Đông Nam'];
const isRoad = (sec) => !!(sec && sec.width > 0 && sec.width <= 120 && sec.sides?.length === 2);
function bearingTo(lat, lng, p) {
  const dx = (p[0] - lng) * Math.cos((lat * Math.PI) / 180), dy = p[1] - lat;
  return ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360;
}
/** A point just beyond the cell boundary (inside the facing cell). */
function beyond(s, side, extra = 2.5) {
  const k = (side.distance + extra) / Math.max(side.distance, 0.1);
  return { lat: s.lat + (side.point[1] - s.lat) * k, lng: s.lng + (side.point[0] - s.lng) * k };
}

function drawSection(s) {
  const sec = s.section;
  if (!isRoad(sec)) { clearSection(); return; }
  if (s.tab === 'road') return;
  const [a, b] = sec.sides.map((x) => x.point);
  pm.setData('section', { type: 'FeatureCollection', features: [
    { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [a, b] } },
    { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: a } },
    { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: b } },
  ] });
  // the tag sits at the end farther from the pin so both stay readable
  const far = sec.sides[0].distance >= sec.sides[1].distance ? a : b;
  pm.setTag({ lng: far[0], lat: far[1] }, `${icon('move-horizontal')}≈ ${fmt.num(sec.width, sec.width < 20 ? 1 : 0)} m`, 'section');
}

function clearSection() {
  pm.setData('section', null);
  pm.setTag(null);
}

/* ---------------- tab: road planning ---------------- */

/** Today's roads (OSM), building footprints (Overture) and plan cells around the pick → analysis (off the main thread). */
function loadRoad(s) {
  s.road = null; // loading
  const t0 = performance.now();
  // compute worker: roads, buildings and plan cells around the pick → analysis (compute/impl.js)
  compute('road', [s.lat, s.lng], { signal: s.ctl.signal })
    .then((r) => {
      if (s !== sel) return;
      r.loadMs = performance.now() - t0;
      s.road = r;
      if (s.tab === 'road') { renderBody(); drawRoad(s); }
    })
    .catch((e) => {
      if (isAbort(e) || s !== sel) return;
      s.road = { error: e.message || 'Lỗi tải dữ liệu' };
      if (s.tab === 'road') renderBody();
    });
}

function roadBody(s) {
  if (s.road === undefined || s.road === null) return roadLoading();
  if (s.road.error) return errorBlockRoad(s.road.error);
  return stagger(roadTab(s.road));
}
const errorBlockRoad = (m) => roadError(m);

function drawRoad(s) {
  const r = s?.road;
  if (!r || r.error || s.tab !== 'road') return;
  pm.setData('road', roadOverlay(r));
  const tag = roadTag(r);
  pm.setTag(tag ? tag.ll : null, tag?.html, 'section');
}

function clearRoad() {
  pm.setData('road', null);
  if (sel?.tab === 'road') pm.setTag(null);
}

function buildingFeature(s, i) {
  return s.road?.geojson?.features.find((f) => f.properties.kind === 'building' && f.properties.i === i) || null;
}

/** Hovering the along-route profile: read out the section and mark it on the map. */
function onRoadChart(e) {
  const s = sel;
  if (!s || s.tab !== 'road' || !s.road?.stations?.length) return;
  const svg = e.target.closest?.('[data-road-chart]');
  const out = $('[data-road-readout]', els.body);
  const cur = $('[data-road-cursor]', els.body);
  if (!out || !cur) return;
  if (!svg || e.type === 'pointerleave') {
    if (e.type === 'pointerleave' && e.target !== svg && e.target !== els.panel) return;
    if (cur.style.display === 'none') return;
    cur.style.display = 'none';
    out.innerHTML = readout(s.road, null);
    drawRoad(s);
    return;
  }
  const b = svg.getBoundingClientRect();
  const { i, x } = stationAt(s.road, (e.clientX - b.left) / b.width);
  const st = s.road.stations[i];
  cur.setAttribute('x1', x.toFixed(1)); cur.setAttribute('x2', x.toFixed(1)); cur.style.display = '';
  out.innerHTML = readout(s.road, st);
  const W = st.W != null && (st.status === 'keep' || st.status === 'widen') ? st.W : null;
  pm.setTag({ lng: st.p[0], lat: st.p[1] }, W ? `${icon('move-horizontal')}≈ ${fmt.num(W, W < 20 ? 1 : 0)} m` : icon('circle-dot'), 'section');
}

/** Hovering a building row outlines that footprint on the map. */
function onHoverBld(e) {
  const s = sel;
  if (!s || s.tab !== 'road') return;
  const row = e.target.closest?.('[data-act="road-bld"]');
  if (!row) return;
  if (e.type === 'pointerout') { if (row.contains(e.relatedTarget)) return; pm.highlight(null, null); return; }
  const f = buildingFeature(s, +row.dataset.i);
  if (f) pm.highlight(f.geometry, null);
}

function roadHero(s) {
  const sec = s.section;
  const level = s.sectionLevel;
  const axis = AXIS4[Math.round(sec.axis / 45) % 4];
  const sides = sec.sides.map((x, i) => ({ ...x, i, dir: COMPASS8[Math.round(bearingTo(s.lat, s.lng, x.point) / 45) % 8] }));
  const wTxt = fmt.num(sec.width, sec.width < 20 ? 1 : 0);
  return `<section class="hero road" data-hero="empty" style="--sw:var(--accent)">
    <div class="hero-top">
      <span class="card-ico road-ico">${icon('route')}</span>
      <div style="min-width:0">
        <div class="hero-title">Đường giao thông theo quy hoạch</div>
        <div class="hero-sub">${esc(levelLabel(level))}</div>
        <div class="badges"><span class="badge accent">${icon('route')}Đất giao thông</span>${level.project?.status ? `<span class="badge ${/phê duyệt/i.test(level.project.status) ? 'ok' : 'warn'}">${icon(/phê duyệt/i.test(level.project.status) ? 'badge-check' : 'clock')}${esc(level.project.status)}</span>` : ''}</div>
      </div>
    </div>
    <div class="metrics">
      <div class="metric"><div class="k">Lộ giới ước tính</div><div class="v">≈ ${wTxt}<small>m</small></div></div>
      <div class="metric"><div class="k">Hướng tuyến</div><div class="v sm">${esc(axis)}</div></div>
    </div>
    <div class="snap">
      <div class="snap-head"><span>Ô đất hai bên đường</span><span>từ điểm chọn</span></div>
      <div role="list" aria-label="Ô đất hai bên đường">
      ${sides.map((x) => `<button type="button" class="snap-row" role="listitem" data-act="side" data-i="${x.i}">
        <span class="swatch" style="--sw:${esc(x.cell.color?.hex || '#94a3b8')}"></span>
        <span class="nm"><b>${esc(x.cell.function || x.cell.category || x.cell.name || 'Ô đất')}</b><span>${x.cell.code ? esc(x.cell.code) + ' · ' : ''}phía ${esc(x.dir)}</span></span>
        <span class="val">${fmt.dist(x.distance)}</span>${icon('chevron-right', 'sm')}
      </button>`).join('')}
      </div>
    </div>
    <button type="button" class="link-row road-link" data-act="road-open">${icon('route')}<span class="grow">Xem quy hoạch cả tuyến: mở rộng, nhà bị ảnh hưởng</span>${icon('chevron-right', 'sm')}</button>
    <p class="hero-foot">${icon('info', 'xs')}Ước tính bằng khoảng cách hẹp nhất giữa ranh giới hai ô đất đối diện qua điểm chọn. Lộ giới, mặt cắt chính thức theo hồ sơ đồ án được duyệt.</p>
  </section>`;
}

function patchHero() {
  if (!sel || sel.tab !== 'plan') return;
  const old = $('[data-hero="empty"]', els.body);
  if (!old) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = emptyHero(sel);
  const n = tmp.firstElementChild;
  n.style.setProperty('--i', 0);
  n.classList.add('swap-in');
  old.replaceWith(n);
}

function emptyHero(s) {
  if (isRoad(s.section)) return roadHero(s);
  const inside = inHanoi(s.lat, s.lng);
  const near = s.nearest;
  const items = near?.items || [];
  let title, sub, extra = '';
  if (!inside) {
    title = 'Vị trí nằm ngoài phạm vi Hà Nội';
    sub = 'Dữ liệu quy hoạch chỉ bao phủ địa giới Thủ đô Hà Nội.';
  } else if (items.length && s.section !== null) {
    title = 'Điểm chọn nằm ngoài các ô đất quy hoạch';
    sub = 'Có thể là lòng đường, vỉa hè hoặc khe giữa các lô (vị trí tìm theo tên đường hay rơi vào đây). Chọn ô đất sát bên để xem quy hoạch:';
    const lvl = LEVEL_META[near.level]?.short || '';
    extra = `<div class="snap">
      <div class="snap-head"><span>Ô đất gần nhất${lvl ? ' · ' + esc(lvl) : ''}</span><span>khoảng cách</span></div>
      <div role="list" aria-label="Ô đất gần nhất">
      ${items.map((it, i) => `<button type="button" class="snap-row" role="listitem" data-act="snap" data-i="${i}">
        <span class="swatch" style="--sw:${esc(it.color?.hex || '#94a3b8')}"></span>
        <span class="nm"><b>${esc(it.function || it.category || it.name || 'Ô đất')}</b><span>${it.code ? esc(it.code) + ' · ' : ''}phía ${esc(it.direction)}</span></span>
        <span class="val">${fmt.dist(it.distance)}</span>${icon('chevron-right', 'sm')}
      </button>`).join('')}
      </div>
    </div>`;
  } else {
    title = 'Chưa có ô đất quy hoạch tại điểm này';
    sub = near === null || s.section === null
      ? 'Điểm có thể nằm trên đường giao thông hoặc mặt nước. Đang phân tích các ô đất lân cận…'
      : 'Điểm có thể nằm trên đường giao thông, mặt nước hoặc khu vực chưa có dữ liệu số hoá. Thử chọn lại vị trí bên trong một ô đất.';
    if (near === null || s.section === null) extra = `<div class="snap"><div class="snap-loading">${icon('loader-circle', 'spin')}<span>${s.section === null ? 'Đang đo mặt cắt đường giữa các ô đất…' : 'Đang tìm ô đất lân cận trong bán kính 40 m…'}</span></div></div>`;
  }
  return `<section class="hero" data-hero="empty" style="--sw:var(--text-3)">
    <div class="hero-top">
      <span class="card-ico" style="width:40px;height:40px">${icon(items.length ? 'map-pin' : 'info')}</span>
      <div>
        <div class="hero-title">${title}</div>
        <div class="hero-sub">${sub}</div>
      </div>
    </div>
    ${extra}
  </section>`;
}

function edgeNote(s, level) {
  const cell = level?.cells[0];
  if (!cell?.geometry) return '';
  const d = distanceToGeometryEdges(s.lng, s.lat, cell.geometry);
  const mpp = 156543.03392 * Math.cos(s.lat * Math.PI / 180) / Math.pow(2, s.zoomAtPick + 1); // metres per CSS px (512 tiles)
  const tol = Math.max(1.5, 6 * mpp);
  if (!(d < tol)) return '';
  return `<div class="note warn">${icon('triangle-alert')}<div>Điểm chọn cách ranh giới ô đất khoảng <b>${fmt.dist(d)}</b>, nhỏ hơn sai số khi chạm/nhấp ở mức phóng hiện tại. Hãy phóng to để kiểm tra lại hoặc dùng <button type="button" class="btn-link" data-act="parcel-draw">Kiểm tra khu đất</button> để tính theo ranh giới thửa.</div></div>`;
}

function levelCard(level, open) {
  const meta = LEVEL_META[level.key];
  const cell = level.cells[0];
  const p = level.project;
  if (!level.covered || (!cell && !level.zoning?.length)) {
    const why = level.covered ? 'Không thuộc ô đất nào của đồ án' : level.key === 'detail' ? 'Chưa có đồ án chi tiết 1/500 tại đây' : level.key === 'zone' ? 'Chưa có đồ án phân khu tại đây' : 'Ngoài phạm vi quy hoạch chung';
    return `<section class="card muted" data-open="false"><header class="card-head"><span class="card-ico">${icon(meta.icon)}</span><div><div class="card-title">${esc(meta.title)}</div><div class="card-sub">${esc(p ? p.name : why)}</div></div></header></section>`;
  }
  const subBits = [];
  if (p?.code) subBits.push(p.code);
  if (p?.scale) subBits.push(p.scale);
  if (p?.status) subBits.push(p.status);
  if (!subBits.length && p?.name) subBits.push(p.name);
  const cats = [];
  for (const c of level.cells) {
    const sub = [c.landType && fold(c.landType) !== fold(c.function || c.category || '') ? c.landType : null, c.code ? `Ô ${c.code}` : null].filter(Boolean).join(' · ');
    cats.push(`<div class="cat"><span class="swatch" style="--sw:${esc(c.color?.hex || '#94a3b8')}"></span><div><div class="cat-name">${esc(c.function || c.category || c.name)}</div>${sub ? `<div class="cat-sub">${esc(sub)}</div>` : ''}</div></div>`);
    cats.push(kv(attrRows(c)));
  }
  for (const z of level.zoning || []) {
    cats.push(`<div class="cat"><span class="swatch" style="--sw:${esc(z.color?.hex || '#94a3b8')}"></span><div><div class="cat-name">${esc(z.category || z.name)}${z.code ? ` (${esc(z.code)})` : ''}</div><div class="cat-sub">Phân vùng chức năng theo quy hoạch chung</div></div></div>`);
  }
  if (p) cats.push(`<button type="button" class="link-row" data-act="project" data-level="${level.key}">${icon('drafting-compass')}<span class="grow">${esc(p.name)}</span><span class="meta">Xem đồ án</span>${icon('chevron-right', 'sm')}</button>`);
  return `<section class="card" data-open="${open}" data-level="${level.key}">
    <header class="card-head" role="button" tabindex="0" aria-expanded="${open}">
      <span class="card-ico">${icon(meta.icon)}</span>
      <div style="min-width:0"><div class="card-title">${esc(meta.title)}</div><div class="card-sub">${esc(cell ? (cell.function || cell.category || cell.name) : subBits.join(' · '))}</div></div>
      ${icon('chevron-down', 'chev')}
    </header>
    <div class="card-body"><div class="card-inner"><div class="card-content">${cats.join('')}</div></div></div>
  </section>`;
}

function qh100Card(q) {
  const meta = LEVEL_META.capital;
  if (q === undefined) {
    return `<section class="card" data-open="true" data-level="capital" id="qh100-card"><header class="card-head"><span class="card-ico">${icon(meta.icon)}</span><div style="flex:1"><div class="card-title">${esc(meta.title)}</div><div class="card-sub">Đang tải dữ liệu…</div></div><span class="spinner on" style="position:static"></span></header><div class="card-body"><div class="card-inner"><div class="card-content"><div class="sk sk-line"></div><div class="sk sk-line" style="width:60%"></div></div></div></div></section>`;
  }
  if (q.error || !q.hit) {
    return `<section class="card muted" data-open="false" id="qh100-card"><header class="card-head"><span class="card-ico">${icon(meta.icon)}</span><div style="flex:1"><div class="card-title">${esc(meta.title)}</div><div class="card-sub">${q.error ? 'Chưa tải được dữ liệu' : 'Không có khoanh đất tại vị trí này'}</div></div>${q.error ? `<button type="button" class="btn sm subtle" data-act="retry-qh100">${icon('rotate-ccw')}Thử lại</button>` : ''}</header></section>`;
  }
  const c = q.hit;
  return `<section class="card" data-open="true" data-level="capital" id="qh100-card">
    <header class="card-head" role="button" tabindex="0" aria-expanded="true"><span class="card-ico">${icon(meta.icon)}</span><div style="min-width:0"><div class="card-title">${esc(meta.title)}</div><div class="card-sub">${esc(c.category || c.name)}</div></div>${icon('chevron-down', 'chev')}</header>
    <div class="card-body"><div class="card-inner"><div class="card-content">
      <div class="cat"><span class="swatch" style="--sw:${esc(c.color?.hex || '#94a3b8')}"></span><div><div class="cat-name">${esc(c.category || c.name)}</div><div class="cat-sub">Chức năng sử dụng đất · tầm nhìn 100 năm</div></div></div>
      ${kv(attrRows(c))}
      <button type="button" class="link-row" data-act="show-layer" data-layer="895">${icon('layers')}<span class="grow">Hiển thị lớp tầm nhìn 100 năm trên bản đồ</span>${icon('chevron-right', 'sm')}</button>
    </div></div></div>
  </section>`;
}

function patchQh100() {
  if (!sel || sel.tab !== 'plan') return;
  const old = $('#qh100-card', els.body);
  if (!old) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = qh100Card(sel.qh100);
  const n = tmp.firstElementChild;
  n.style.setProperty('--i', 0);
  old.replaceWith(n);
}

/** Land-use plan (KHSDĐ, Luật Đất đai) card: raster maps only, so it points to the map layer. */
function sddCard(s) {
  const meta = PLAN_BY_NO[SDD];
  const cat = sddCatalog();
  if (!cat) loadSddCatalog().then(patchSdd, () => {});
  const period = cat && sddPeriod();
  const names = period ? [...new Set(sddLayersAt(s.lat, s.lng, period).map((l) => l[1]))] : [];
  const sub = !cat ? 'Đang tải danh mục bản đồ…' : names.length ? `${period.short} · ${names.join(', ')}` : `Ngoài phạm vi bản đồ ${period.short}`;
  const open = prefs.plan === SDD; // expanded while the land-use plan map is the active view
  return `<section class="card" data-open="${open}" id="sdd-card">
    <header class="card-head" role="button" tabindex="0" aria-expanded="${open}"><span class="card-ico">${icon(meta.icon)}</span><div style="min-width:0"><div class="card-title">${esc(meta.title)}</div><div class="card-sub">${esc(sub)}</div></div>${icon('chevron-down', 'chev')}</header>
    <div class="card-body"><div class="card-inner"><div class="card-content">
      <p class="card-text">Bản đồ kế hoạch sử dụng đất hằng năm cấp huyện (Luật Đất đai) của Sở Nông nghiệp và Môi trường. Mã loại đất (ODT, ONT, DGT, LUA…) ghi trực tiếp trên bản đồ; hệ thống của Sở chưa cung cấp tra cứu theo điểm nên ứng dụng chỉ hiển thị bản đồ.</p>
      <p class="card-text muted">Khác quy hoạch xây dựng ở trên: KHSDĐ xác định chỉ tiêu, loại đất được chuyển mục đích, thu hồi trong năm; không quy định tầng cao, mật độ xây dựng.</p>
      <button type="button" class="link-row" data-act="show-layer" data-layer="${SDD}">${icon('layers')}<span class="grow">Xem bản đồ kế hoạch sử dụng đất tại đây</span>${icon('chevron-right', 'sm')}</button>
      <a class="link-row" href="https://qhkhsdd.hanoi.gov.vn" target="_blank" rel="noopener">${icon('external-link')}<span class="grow">Tra cứu chính thức: qhkhsdd.hanoi.gov.vn</span>${icon('chevron-right', 'sm')}</a>
    </div></div></div>
  </section>`;
}

function patchSdd() {
  if (!sel || sel.tab !== 'plan' || !sel.result) return;
  const old = $('#sdd-card', els.body);
  if (!old) return;
  const open = old.dataset.open;
  const tmp = document.createElement('div');
  tmp.innerHTML = sddCard(sel);
  const n = tmp.firstElementChild;
  n.style.setProperty('--i', 0);
  n.dataset.open = open;
  n.querySelector('.card-head')?.setAttribute('aria-expanded', open);
  old.replaceWith(n);
}

function planTab(s) {
  if (s.error) return errorBlock(s.error.message);
  if (!s.result) return skeleton() + qh100Card(s.qh100).replace('<section', '<section style="--i:4"');
  const levels = s.result.levels;
  const primary = levels.find((l) => l.cells.length);
  const parts = [];
  parts.push(primary ? hero(primary) : emptyHero(s));
  if (primary && !s.snapped) parts.push(edgeNote(s, primary));
  if (primary && s.kind !== 'parcel') parts.push(`<button type="button" class="link-row road-link card-like" data-act="road-open">${icon('route')}<span class="grow"><b>Quy hoạch giao thông</b><span>Lộ giới tuyến gần nhất, đoạn mở rộng, nhà bị ảnh hưởng</span></span>${icon('chevron-right', 'sm')}</button>`);
  if (s.kind !== 'parcel') parts.push(`<div id="gia-teaser" class="gia-teaser-host">${giaTeaser(s)}</div>`);
  parts.push(`<div class="section-title"><span>Các cấp quy hoạch tại điểm</span></div>`);
  for (const l of levels) parts.push(levelCard(l, l === primary));
  parts.push(qh100Card(s.qh100));
  parts.push(sddCard(s));
  parts.push(`<div class="note">${icon('info')}<div>Tra cứu trực tiếp từ <a href="https://quyhoach.hanoi.gov.vn" target="_blank" rel="noopener">Hệ thống thông tin quy hoạch TP Hà Nội</a> (đang vận hành thử nghiệm). Thông tin chỉ có giá trị tham khảo, không thay thế văn bản cung cấp thông tin quy hoạch của cơ quan có thẩm quyền.<div class="meta-line">${icon('clock', 'xs')} ${fmt.ms(s.ms || s.result.ms || 0)} · ${s.result.source === 'official' ? 'truy vấn điểm chính thức' : 'dữ liệu ô đất (đã đối chiếu với truy vấn chính thức)'}</div></div></div>`);
  return stagger(parts);
}

/* ---------------- tab: projects & documents ---------------- */

function projectRows(p) {
  const rows = [];
  const add = (k, v) => { if (v != null && v !== '') rows.push([k, String(v)]); };
  add('Loại quy hoạch', p.type);
  add('Tỷ lệ', p.scale);
  add('Trạng thái', p.status);
  add('Quyết định phê duyệt', p.decision ? `${p.decision}${p.decisionDate ? ' ngày ' + p.decisionDate : ''}` : p.decisionDate);
  add('Cơ quan phê duyệt', p.approver);
  add('Quyết định nhiệm vụ', p.taskDecision ? `${p.taskDecision}${p.taskDecisionDate ? ' ngày ' + p.taskDecisionDate : ''}` : null);
  add('Diện tích đồ án', Number.isFinite(p.areaHa) ? `${fmt.num(p.areaHa, 2)} ha` : null);
  add('Quy mô dân số', Number.isFinite(p.population) ? `${fmt.num(p.population)} người${p.popYear ? ` (đến năm ${p.popYear})` : ''}` : null);
  add('Phường / xã', p.ward);
  add('Phạm vi', p.location);
  add('Hình thức', p.adjust);
  add('Giai đoạn', p.phase);
  add('Đơn vị lập', p.maker);
  add('Chủ đầu tư', p.investor);
  add('Ghi chú', p.note);
  add('Mã hồ sơ', p.planCode);
  return rows;
}

function docsTab(s) {
  if (s.error) return errorBlock(s.error.message);
  if (!s.result) return skeleton();
  const projects = s.result.levels.filter((l) => l.project);
  const catalogLink = `<button type="button" class="link-row cat-entry" data-act="catalog">${icon('library-big')}<span class="grow">Danh mục tất cả đồ án quy hoạch<small>Tìm theo mã, tên, số quyết định · kể cả đồ án chưa số hoá</small></span>${icon('chevron-right', 'sm')}</button>`;
  if (!projects.length) return stagger([`<div class="note">${icon('info')}<div>Không có đồ án quy hoạch nào chứa vị trí này.</div></div>`, catalogLink]);
  const first = Math.max(0, projects.findIndex((l) => s.focus && l.project.id === s.focus));
  const parts = projects.map((l, i) => {
    const p = l.project;
    const open = i === first;
    const docs = (p.docs || []).map((d) => `<a class="link-row" href="${esc(d.url)}" target="_blank" rel="noopener">${icon(/\.(png|jpe?g|tif)/i.test(d.name) ? 'file-image' : 'file-text')}<span class="grow">${esc(d.name.replace(/[_]+/g, ' '))}</span><span class="meta">${esc(d.kind || '')}</span>${icon('external-link', 'sm')}</a>`).join('');
    const c = p.catalog;
    const pub = (c?.links || []).map((d) => `<a class="link-row" href="${esc(d.url)}" target="_blank" rel="noopener">${icon(d.kind === 'pdf' ? 'file-text' : d.kind === 'page' ? 'globe' : 'file-image')}<span class="grow">${esc(d.label)}</span><span class="meta">${esc(d.src || '')}</span>${icon('external-link', 'sm')}</a>`).join('');
    return `<section class="card" data-open="${open}" data-level="${l.key}">
      <header class="card-head" role="button" tabindex="0" aria-expanded="${open}">
        <span class="card-ico">${icon(LEVEL_META[l.key].icon)}</span>
        <div style="min-width:0"><div class="card-title">${esc(p.name)}</div><div class="card-sub">${esc([LEVEL_META[l.key].short, p.code, p.status].filter(Boolean).join(' · '))}</div></div>
        ${icon('chevron-down', 'chev')}
      </header>
      <div class="card-body"><div class="card-inner"><div class="card-content">
        ${kv(projectRows(p))}
        ${c?.note ? `<div class="note">${icon('info')}<div>${esc(c.note)}</div></div>` : ''}
        ${docs ? `<div class="section-title" style="margin:2px 0 -4px"><span>Hồ sơ, văn bản (${p.docs.length})</span></div>${docs}` : '<div class="empty-line">' + icon('file') + ' Chưa có văn bản đính kèm trên hệ thống</div>'}
        ${pub ? `<div class="section-title" style="margin:2px 0 -4px"><span>Quyết định, bản vẽ công bố (${c.links.length})</span></div>${pub}` : ''}
        ${p.bbox ? `<button type="button" class="btn sm subtle" data-act="project" data-level="${l.key}">${icon('scan-search')}Xem ranh giới đồ án trên bản đồ</button>` : ''}
      </div></div></div>
    </section>`;
  });
  parts.push(catalogLink);
  parts.push(`<div class="note">${icon('info')}<div>Hồ sơ, văn bản liên kết trực tiếp tới kho hồ sơ của hệ thống thông tin quy hoạch thành phố; quyết định, bản vẽ công bố lấy từ Cổng thông tin quy hoạch (Bộ Xây dựng) và Viện Quy hoạch xây dựng Hà Nội.</div></div>`);
  return stagger(parts);
}

/* ---------------- tab: nearby ---------------- */

/**
 * Upstream POI types are coarse (a hospital department is often typed "education"), so the
 * name decides first, in priority order: strong health words beat "đại học" (Bệnh viện Đại học Y),
 * strong university words beat "khoa" (Khoa Y – Đại học …), schools beat ward names, etc.
 * The most specific upstream type is only a fallback.
 */
const NAME_RULES = [
  [/bệnh viện|phòng khám|khám bệnh|khám chữa bệnh|trạm y tế|trung tâm y tế|nhà thuốc|hiệu thuốc|quầy thuốc|nha khoa|hospital|clinic|pharmacy|dental/i, 'health'],
  [/đại học|học viện|cao đẳng|trung cấp|university|college|academy/i, 'uni'],
  [/mầm non|mẫu giáo|nhà trẻ|tiểu học|thcs|thpt|trung học|liên cấp|preschool|kindergarten|montessori|primary school|secondary school|high school/i, 'edu'],
  [/^khoa\s+(?:khám|xét nghiệm|nội|ngoại|sản|nhi|tai|mắt|răng|da\s|chấn thương|cấp cứu|hồi sức|gây mê|tim|thần kinh|ung bướu|truyền nhiễm|dinh dưỡng|dược|chẩn đoán|phục hồi|y học|huyết học|hô hấp|tiêu hoá|tiêu hóa|thận)|xét nghiệm|nam học|sản phụ|nhi khoa|da liễu|đông y|y học cổ truyền|y tế|chẩn đoán|vật lý trị liệu|phục hồi chức năng/i, 'health'],
  [/^khoa\s|faculty|(?:^|[\s(-])đh(?:[\s.,)-]|$)|ký túc xá|trung tâm (?:đào tạo|ngoại ngữ|tiếng|giáo dục|anh ngữ|kỹ năng|tin học)|anh ngữ|english|ngoại ngữ|ielts|toeic|dạy nghề|đào tạo|luyện thi|gia sư|tin học|online|edu\b/i, 'uni'],
  [/^trường\s/i, 'edu'],
  [/chợ|siêu thị|trung tâm thương mại|tttm|vinmart|winmart|market|mall|mart\b/i, 'market'],
  [/ubnd|ủy ban|uỷ ban|công an|hành chính|tòa án|toà án|viện kiểm sát|kho bạc|chi cục|bảo hiểm xã hội|trụ sở/i, 'gov'],
];
const TYPE_RULES = [
  [/^(?:preschool|primary_school|secondary_school|high_school|school)$/, 'edu'],
  [/^(?:university|college|training_center|language_center)$/, 'uni'],
  [/^(?:hospital|health|doctor|clinic|pharmacy|dentist)$/, 'health'],
  [/^(?:local_market|supermarket|shopping_mall|market|grocery_store|store)$/, 'market'],
  [/^(?:committee|government|police|townhall|city_hall|local_government_office)$/, 'gov'],
];
function groupOf(it) {
  const name = String(it.name || '');
  for (const [re, k] of NAME_RULES) if (re.test(name)) return k;
  const types = (it.types?.length ? it.types : [it.type]).filter(Boolean);
  for (const [re, k] of TYPE_RULES) if (types.some((t) => re.test(t))) return k;
  if (types.includes('education')) return 'uni';
  return 'other';
}

/**
 * Transport set (bus stops, stations, parking, fuel): the upstream type is reliable here and the
 * names are locations ("Đối diện 198 Trần Quang Khải"), so classify by type only.
 */
function transportGroup(it) {
  const name = String(it.name || '').trim();
  const types = (it.types?.length ? it.types : [it.type]).filter(Boolean);
  if (name.length < 4 || /co\.?,?\s*ltd|company|công ty|travel|du lịch|tour/i.test(name)) return null;
  if (types.some((t) => /^(?:train_station|subway_station|light_rail_station|railway_station)$/.test(t)) || /^ga\s|nhà ga|metro/i.test(name)) return 'rail';
  if (types.includes('gas_station')) return 'fuel';
  if (types.includes('parking')) return 'parking';
  if (types.some((t) => /^(?:transit_station|bus_station|bus_stop)$/.test(t))) return 'bus';
  return null;
}
const transportName = (it) => (/^\d[\d\s,;.-]*$/.test(it.name.trim()) ? `Điểm dừng tuyến ${it.name.trim()}` : it.name);

/** Deduplicate by folded name (keep nearest) and drop near-duplicate points. */
function dedupeNearby(items) {
  const out = [];
  const byName = new Map();
  for (const it of [...items].sort((a, b) => a.distance - b.distance)) {
    const key = fold(it.name).replace(/\b(truong|dai hoc|ha noi|hanoi|toa|nha)\b/g, '').replace(/\s+/g, ' ').trim();
    if (!key) continue;
    if (byName.has(key)) continue;
    const close = out.find((o) => Math.abs(o.lat - it.lat) < 2.5e-4 && Math.abs(o.lng - it.lng) < 2.5e-4 && fold(o.name).slice(0, 12) === fold(it.name).slice(0, 12));
    if (close) continue;
    byName.set(key, it);
    out.push(it);
  }
  return out;
}

/** Land price of the pick (static tables, no request to the planning system). */
function loadGia(s) {
  loadGiaFor(s, (x) => {
    if (x !== sel) return;
    if (x.tab === 'gia') renderBody();
    else if (x.tab === 'plan') patchGiaTeaser();
  });
  if (s.tab === 'gia') renderBody();
}

function patchGiaTeaser() {
  const h = sel && $('#gia-teaser', els.body);
  if (h) h.innerHTML = giaTeaser(sel);
}

function loadNearby(s) {
  s.nearby = null; // loading
  if (s.tab === 'near') renderBody();
  const o = { signal: s.ctl.signal };
  const transport = api.nearby(s.lat, s.lng, s.radius, o, 'transport').then((j) => j.items || [], (e) => { if (isAbort(e)) throw e; return []; });
  Promise.all([api.nearby(s.lat, s.lng, s.radius, o), transport]).then(([j, tr]) => {
    if (s !== sel) return;
    const fac = dedupeNearby(j.items || []).map((it) => ({ ...it, group: groupOf(it) }));
    const ids = new Set(fac.map((it) => it.id));
    const trs = dedupeNearby(tr.filter((it) => !ids.has(it.id)))
      .map((it) => ({ ...it, group: transportGroup(it) }))
      .filter((it) => it.group)
      .map((it) => ({ ...it, name: transportName(it) }));
    s.nearby = [...fac, ...trs].sort((a, b) => a.distance - b.distance);
    renderTabs();
    if (s.tab === 'near') { renderBody(); showPoiMarkers(); }
  }).catch((e) => {
    if (isAbort(e) || s !== sel) return;
    s.nearby = { error: e.message };
    if (s.tab === 'near') renderBody();
  });
}

function showPoiMarkers() {
  if (!sel || sel.tab !== 'near' || !Array.isArray(sel.nearby)) return;
  const icons = Object.fromEntries(NEARBY_GROUPS.map((g) => [g.key, g.icon]));
  pm.setPois(sel.nearby.slice(0, 40).map((it) => ({ ...it, icon: icons[it.group] })), (it) => {
    pm.goTo({ lat: it.lat, lng: it.lng }, { zoom: Math.max(pm.map.getZoom(), 17), padding: viewPadding() });
    toast(`${it.name} · cách ${fmt.dist(it.distance)}`, { type: 'info', ms: 2200 });
  });
}

function nearTab(s) {
  const radiusSeg = `<div class="seg" role="tablist" aria-label="Bán kính" style="margin:0">${[300, 500, 1000].map((r) => `<button type="button" role="tab" data-act="radius" data-r="${r}" aria-selected="${s.radius === r}">${r < 1000 ? r + ' m' : '1 km'}</button>`).join('')}</div>`;
  const head = `<div class="section-title" style="margin-top:0"><span>Tiện ích trong bán kính</span>${radiusSeg}</div>`;
  if (s.nearby === undefined || s.nearby === null) {
    return stagger([head, '<div class="sk sk-block" style="height:120px"></div>', '<div class="sk sk-block" style="height:90px"></div>']);
  }
  if (s.nearby.error) return stagger([head, `<div class="note warn">${icon('triangle-alert')}<div>${esc(s.nearby.error)}</div></div>`]);
  if (!s.nearby.length) return stagger([head, `<div class="note">${icon('info')}<div>Không có trường học, cơ sở y tế, chợ, cơ quan hành chính hay điểm giao thông công cộng nào trong bán kính đã chọn.</div></div>`]);
  const parts = [head];
  let trHead = false;
  for (const g of NEARBY_GROUPS) {
    const list = s.nearby.filter((it) => it.group === g.key);
    if (!list.length) continue;
    if (g.transport && !trHead) {
      trHead = true;
      const n = s.nearby.filter((it) => NEARBY_GROUPS.find((x) => x.key === it.group)?.transport).length;
      parts.push(`<div class="near-divider"><span>${icon('route', 'xs')}Giao thông</span><span>${n} điểm</span></div>`);
    }
    const items = list.map((it, i) => `<button type="button" class="near-item" data-act="poi" data-id="${esc(it.id)}"${i >= 5 ? ' hidden data-more="1"' : ''}><span class="near-ico">${icon(g.icon)}</span><span class="near-name">${esc(it.name)}</span><span class="near-dist">${fmt.dist(it.distance)}</span></button>`).join('');
    parts.push(`<section class="near-group"><div class="section-title"><span>${esc(g.title)}</span><span>${list.length}</span></div>${items}${list.length > 5 ? `<button type="button" class="btn sm subtle" data-act="more" style="align-self:flex-start;margin-top:4px">${icon('chevron-down')}Xem thêm ${list.length - 5}</button>` : ''}</section>`);
  }
  parts.push(`<div class="note">${icon('info')}<div>Khoảng cách theo đường chim bay (geodesic WGS84). Danh sách địa điểm lấy từ hệ thống thông tin quy hoạch thành phố.</div></div>`);
  return stagger(parts);
}

/* ---------------- tab: parcel analysis ---------------- */

function parcelTab() {
  if (!analysis) {
    return stagger([
      `<section class="hero" style="--sw:var(--accent)"><div class="hero-top"><span class="card-ico" style="width:40px;height:40px">${icon('land-plot')}</span><div><div class="hero-title">Kiểm tra quy hoạch cho cả khu đất</div><div class="hero-sub">Vẽ ranh giới hoặc dán bảng toạ độ VN-2000 trên sổ đỏ để tính diện tích từng loại đất quy hoạch nằm trong thửa, theo mọi cấp quy hoạch.</div></div></div>
        <div class="tool-actions" style="justify-content:flex-start"><button type="button" class="btn primary sm" data-act="parcel-draw">${icon('pen-line')}Vẽ trên bản đồ</button><button type="button" class="btn sm" data-act="parcel-paste">${icon('clipboard-paste')}Nhập toạ độ VN-2000</button></div></section>`,
      `<div class="note">${icon('lightbulb')}<div>Toạ độ VN-2000 trên giấy chứng nhận tại Hà Nội dùng kinh tuyến trục 105°00′, múi chiếu 3°. Diện tích được tính trên mặt phẳng chiếu này – cùng cách tính với bản đồ địa chính.</div></div>`,
    ]);
  }
  const r = analysis.result;
  const P = r.parcel;
  const layer = r.layers.find((l) => l.layerNo === analysis.layerNo) || r.layers[0];
  const covered = r.layers.filter((l) => l.covered > 0);
  const seg = `<div class="seg fill" role="tablist" aria-label="Cấp quy hoạch">${r.layers.map((l) => `<button type="button" role="tab" data-act="alayer" data-layer="${l.layerNo}" aria-selected="${l.layerNo === layer.layerNo}"${l.covered > 0 ? '' : ' disabled style="opacity:.45"'} data-tip="${esc(PLAN_BY_NO[l.layerNo]?.title || '')}">${esc(shortLayer(l.layerNo))}</button>`).join('')}</div>`;
  const bar = layer.categories.length ? `<div class="bar">${layer.categories.map((c) => `<span style="--sw:${esc(c.color?.hex || '#94a3b8')};flex-grow:${c.percent.toFixed(3)}" data-tip="${esc(c.category)}: ${fmt.pct(c.percent)}"></span>`).join('')}${layer.coveredPercent < 99.95 ? `<span style="--sw:transparent;flex-grow:${(100 - layer.coveredPercent).toFixed(3)}"></span>` : ''}</div>` : '';
  const rows = layer.categories.map((c) => {
    const codes = layer.items.filter((it) => it.category === c.category).map((it) => it.code).filter(Boolean);
    return `<div class="arow" data-act="acat" data-cat="${esc(c.category)}" tabindex="0"><span class="swatch" style="--sw:${esc(c.color?.hex || '#94a3b8')}"></span><span class="nm"><b>${esc(c.category)}</b><span>${c.count} ô${codes.length ? ' · ' + esc(codes.slice(0, 4).join(', ')) + (codes.length > 4 ? '…' : '') : ''}</span></span><span class="val">${fmt.num(c.area, 1)} m²<small>${fmt.pct(c.percent)}</small></span></div>`;
  }).join('');
  const uncovered = Math.max(0, P.area - layer.covered);
  const road = roadPart(layer, P);
  const roadCard = road ? `<section class="hero road-hit" style="--sw:#dc2626"><div class="hero-top"><span class="card-ico" style="width:40px;height:40px;color:#dc2626">${icon('triangle-alert')}</span><div style="min-width:0"><div class="hero-title">${road.area < 0.5 ? 'Không vướng đường quy hoạch' : 'Phần nằm trong đường quy hoạch'}</div><div class="hero-sub">${road.area < 0.5 ? 'Toàn bộ khu đất nằm trong ô đất của đồ án' : 'Phần gạch chéo đỏ trên bản đồ: ngoài mọi ô đất của đồ án' + (road.cells ? ' hoặc thuộc ô đất giao thông' : '') + ' – thường là đất đường (lộ giới) theo quy hoạch'}</div></div></div>${road.area < 0.5 ? '' : `<div class="metrics"><div class="metric"><div class="k">Diện tích vướng</div><div class="v" style="color:#dc2626">${fmt.num(road.area, 1)}<small>m²</small></div></div><div class="metric"><div class="k">Tỷ lệ khu đất</div><div class="v" style="color:#dc2626">${fmt.pct(road.percent)}</div></div><div class="metric"><div class="k">Còn lại trong ô đất</div><div class="v">${fmt.num(Math.max(0, P.area - road.area), 1)}<small>m²</small></div></div></div>`}</section>` : '';
  const cover = layer.covered <= 0
    ? `<div class="note">${icon('info')}<div>Khu đất không giao với ô đất nào của lớp này.</div></div>`
    : layer.coveredPercent < 99.5 ? `<div class="note">${icon('info')}<div>${fmt.m2(uncovered)} (${fmt.pct(100 - layer.coveredPercent)}) không thuộc ô đất nào của lớp này – thường là phần đường giao thông hoặc khu vực chưa số hoá.</div></div>` : '';
  const items = layer.items.map((it) => `<div class="arow" data-act="aitem" data-id="${esc(it.id)}" tabindex="0"><span class="swatch" style="--sw:${esc(it.color?.hex || '#94a3b8')}"></span><span class="nm"><b>${esc(it.code ? it.code + ' · ' : '')}${esc(it.function || it.category)}</b><span>${esc(it.attrs?.filter((a) => /tangCaoMax|matDoXayDungMax|heSoSDD/.test(a.key)).map((a) => `${a.label.replace(' tối đa', '')} ${val(a)}${a.unit === '%' ? '%' : a.unit ? ' ' + a.unit : ''}`).join(' · ') || '')}</span></span><span class="val">${fmt.num(it.area, 1)} m²<small>${fmt.pct(it.percent)}</small></span></div>`).join('');
  const vt = `<table class="vtable"><thead><tr><th>Điểm</th><th>X (m)</th><th>Y (m)</th><th>Cạnh (m)</th></tr></thead><tbody>${P.vertices.map((v, i) => `<tr><td>${v.n}</td><td>${formatVN(v.x, 2)}</td><td>${formatVN(v.y, 2)}</td><td>${P.edges[i] ? fmt.num(P.edges[i].length, 2) : ''}</td></tr>`).join('')}</tbody></table>`;
  return stagger([
    `<section class="hero" style="--sw:var(--accent)"><div class="hero-top"><span class="card-ico" style="width:40px;height:40px">${icon('land-plot')}</span><div style="min-width:0"><div class="hero-title">Phân tích khu đất</div><div class="hero-sub">${covered.length} lớp quy hoạch giao với khu đất · ${fmt.ms(r.stats?.ms || 0)}</div></div></div>
      <div class="metrics"><div class="metric"><div class="k">Diện tích <span class="nw">(VN-2000)</span></div><div class="v">${fmt.num(P.area, 1)}<small>m²</small></div></div><div class="metric"><div class="k">Chu vi</div><div class="v">${fmt.num(P.perimeter, 2)}<small>m</small></div></div><div class="metric"><div class="k">Số đỉnh</div><div class="v">${P.vertices.length}</div></div></div></section>`,
    roadCard,
    `<div>${seg}${bar}<div class="alist">${rows || '<div class="empty-line">' + icon('info') + ' Không có dữ liệu</div>'}</div></div>`,
    road ? '' : cover,
    layer.items.length ? `<section class="card" data-open="false"><header class="card-head" role="button" tabindex="0" aria-expanded="false"><span class="card-ico">${icon('list')}</span><div><div class="card-title">Chi tiết từng ô đất (${layer.items.length})</div><div class="card-sub">Diện tích phần ô đất nằm trong khu đất</div></div>${icon('chevron-down', 'chev')}</header><div class="card-body"><div class="card-inner"><div class="card-content"><div class="alist">${items}</div></div></div></div></section>` : '',
    `<section class="card" data-open="false"><header class="card-head" role="button" tabindex="0" aria-expanded="false"><span class="card-ico">${icon('table')}</span><div><div class="card-title">Toạ độ đỉnh thửa (VN-2000)</div><div class="card-sub">${P.zone === 'utm-48' ? 'KTT 105°, múi 6°' : 'KTT 105°00′, múi 3°'} · ${P.vertices.length} điểm</div></div>${icon('chevron-down', 'chev')}</header><div class="card-body"><div class="card-inner"><div class="card-content">${vt}</div></div></div></section>`,
    `<div class="tool-actions" style="justify-content:flex-start;margin-top:0"><button type="button" class="btn sm" data-act="acopy">${icon('clipboard-copy')}Sao chép bảng</button><button type="button" class="btn sm" data-act="acsv">${icon('download')}Tải CSV</button><button type="button" class="btn sm" data-act="sheet" aria-haspopup="dialog">${icon('file-down')}Phiếu PDF</button><button type="button" class="btn sm subtle" data-act="aedit">${icon('pen-line')}Sửa ranh giới</button><span class="grow"></span><button type="button" class="btn sm subtle" data-act="aclear">${icon('trash')}Xoá</button></div>`,
    `<div class="note">${icon('info')}<div>Diện tích từng phần được tính bằng phép giao đa giác chính xác (tam giác hoá + cắt Sutherland–Hodgman) trên mặt phẳng VN-2000, từ ranh giới ô đất gốc của hệ thống thông tin quy hoạch.</div></div>`,
  ]);
}

const shortLayer = (n) => ({ 673: '1/500', 654: 'Phân khu', 666: 'QH chung', 667: 'Phân vùng', 895: '100 năm' }[n] || PLAN_BY_NO[n]?.short || n);

const ROAD_CAT = (c) => /giao thông|đường/i.test(c || '') && !/bãi|bến|đỗ|depot|ga /i.test(c || '');
/** Planned-road part of the parcel: outside every cell of the plan, plus cells of road land. */
function roadPart(layer, P) {
  if (!layer || !(layer.covered > 0) || !['zone', 'detail', 'general'].includes(layer.level)) return null;
  const cells = layer.categories.filter((c) => ROAD_CAT(c.category)).reduce((s, c) => s + c.area, 0);
  const area = Math.max(0, P.area - layer.covered) + cells;
  return { area, percent: (area / P.area) * 100, cells };
}

function drawPieces() {
  if (!analysis) { pm.setData('pieces', null); return; }
  const layer = analysis.result.layers.find((l) => l.layerNo === analysis.layerNo);
  const f = analysis.focus;
  const features = [];
  const P = analysis.result.parcel;
  if (roadPart(layer, P)?.area >= 0.5 && P.vertices?.length >= 3) {
    const ring = P.vertices.map((v) => [v.lng, v.lat]);
    features.push({ type: 'Feature', properties: { kind: 'road' }, geometry: { type: 'Polygon', coordinates: [[...ring, ring[0]]] } });
  }
  for (const it of layer?.items || []) {
    if (!it.pieces?.length) continue;
    const dim = f ? (f.type === 'cat' ? it.category !== f.value : it.id !== f.value) : false;
    features.push({ type: 'Feature', properties: { color: it.color?.hex || '#94a3b8', dim, id: it.id, category: it.category, roadcell: ROAD_CAT(it.category) }, geometry: { type: 'MultiPolygon', coordinates: it.pieces } });
  }
  pm.setData('pieces', { type: 'FeatureCollection', features });
}

function analysisTable() {
  const r = analysis.result;
  const lines = [['Lớp quy hoạch', 'Loại đất / chức năng', 'Ký hiệu ô', 'Diện tích (m²)', 'Tỷ lệ (%)']];
  for (const l of r.layers) for (const it of l.items) lines.push([l.title, it.function || it.category, it.code || '', it.area.toFixed(2), it.percent.toFixed(2)]);
  lines.push([]);
  lines.push(['Điểm', 'X (m)', 'Y (m)', 'Kinh độ', 'Vĩ độ']);
  for (const v of r.parcel.vertices) lines.push([v.n, v.x.toFixed(3), v.y.toFixed(3), v.lng.toFixed(7), v.lat.toFixed(7)]);
  lines.push([]);
  lines.push(['Diện tích khu đất (m²)', r.parcel.area.toFixed(2)]);
  lines.push(['Chu vi (m)', r.parcel.perimeter.toFixed(2)]);
  return lines;
}

/* ======================================================================
   interactions
   ====================================================================== */

function toggleCard(card) {
  if (!card || card.classList.contains('muted')) return;
  const open = card.dataset.open !== 'true';
  card.dataset.open = open;
  card.querySelector('.card-head')?.setAttribute('aria-expanded', open);
}

function levelByKey(key) { return sel?.result?.levels.find((l) => l.key === key); }

function applyHighlight() {
  const r = sel?.result;
  if (!r || sel.kind === 'parcel') return;
  const primary = r.levels.find((l) => l.cells.length);
  pm.highlight(primary?.cells[0]?.geometry || null, sel.projectGeom || null);
}

function onClick(e) {
  const t = e.target.closest('[data-act],[data-tab],.card-head');
  if (!t || !sel) return;
  if (t.dataset.tab) return setTab(t.dataset.tab);
  if (!t.dataset.act && t.classList.contains('card-head')) return toggleCard(t.closest('.card'));
  const s = sel;
  if (t.dataset.act?.startsWith('gia-') && giaClick(t, s, { padding: viewPadding, rerender: (reload) => { if (reload) loadGia(s); else if (s.tab === 'gia') renderBody(); } })) return;
  switch (t.dataset.act) {
    case 'close': closePanel(); break;
    case 'share':
      act('share', 'open');
      lazyImport(() => import('./share.js')).then((m) => m.openShare({ pm, sel: s, info: placeInfo(s), analysis })).catch(() => {});
      break;
    case 'sheet':
      act('sheet', 'open');
      lazyImport(() => import('./sheet.js')).then((m) => m.openSheet({ pm, sel: s, analysis })).catch(() => {});
      break;
    case 'save': toggleSaved(s); break;
    case 'copy-wgs': act('copy', 'wgs'); copyText(`${s.lat.toFixed(6)}, ${s.lng.toFixed(6)}`, 'toạ độ WGS84'); break;
    case 'copy-vn': { act('copy', 'vn2000'); const v = vnOf(s); copyText(`X=${v.x.toFixed(3)} Y=${v.y.toFixed(3)}`, 'toạ độ VN-2000'); break; }
    case 'copy-summary': act('copy', 'summary'); copyText(summaryText(), 'thông tin quy hoạch'); break;
    case 'retry': openAt({ lat: s.lat, lng: s.lng }, { label: s.address?.title, sub: s.address?.sub, fly: false, keepTab: true }); break;
    case 'retry-qh100':
      s.qh100 = undefined; patchQh100();
      api.qh100(s.lat, s.lng, { signal: s.ctl.signal }).then((r) => { if (s === sel) { s.qh100 = r; patchQh100(); } }).catch((er) => { if (!isAbort(er) && s === sel) { s.qh100 = { error: er.message }; patchQh100(); } });
      break;
    case 'catalog': {
      const fp = s.result?.levels.find((l) => l.project?.catalog && (!s.focus || l.project.id === s.focus))?.project;
      bus.emit('catalog', fp ? { id: fp.catalog.id } : {});
      break;
    }
    case 'project': {
      const l = levelByKey(t.dataset.level);
      const p = l?.project;
      if (!p) break;
      if (s.tab !== 'docs' && t.classList.contains('link-row')) { setTab('docs'); }
      pm.highlight(l.cells[0]?.geometry || null, p.geometry || null);
      if (p.bbox) pm.fitBBox(p.bbox, { padding: viewPadding(), maxZoom: 16 });
      break;
    }
    case 'snap': {
      const it = s.nearest?.items?.[+t.dataset.i];
      if (!it) break;
      openAt(it.entry, { label: s.address?.title, sub: s.address?.sub, kind: s.kind, keepTab: true, snapped: true, zoom: Math.max(pm.map.getZoom(), 17) });
      break;
    }
    case 'side': {
      const x = s.section?.sides?.[+t.dataset.i];
      if (!x) break;
      openAt(beyond(s, x), { label: s.address?.title, sub: s.address?.sub, kind: s.kind, keepTab: true, snapped: true, fly: false });
      break;
    }
    case 'show-layer': bus.emit('set-plan', planFrom(t.dataset.layer)); break;
    case 'road-open': setTab('road'); break;
    case 'road-retry': loadRoad(s); renderBody(); break;
    case 'road-bld': {
      const f = buildingFeature(s, +t.dataset.i);
      if (!f) break;
      const c = s.road.buildings.top[+t.dataset.i].c;
      pm.highlight(f.geometry, null);
      pm.fitBBox([c[0] - c[2] * 3, c[1] - c[3] * 3, c[0] + c[2] * 3, c[1] + c[3] * 3], { padding: viewPadding(), maxZoom: 19 });
      break;
    }
    case 'parcel-draw': bus.emit('tool', { name: 'parcel', mode: 'draw' }); break;
    case 'parcel-paste': bus.emit('tool', { name: 'parcel', mode: 'paste' }); break;
    case 'radius': {
      const r = +t.dataset.r;
      if (r !== s.radius) { s.radius = r; loadNearby(s); }
      break;
    }
    case 'more': {
      const g = t.closest('.near-group');
      for (const b of $$('[data-more]', g)) b.hidden = false;
      t.remove();
      break;
    }
    case 'poi': {
      const it = Array.isArray(s.nearby) && s.nearby.find((x) => String(x.id) === t.dataset.id);
      if (it) pm.goTo({ lat: it.lat, lng: it.lng }, { zoom: Math.max(pm.map.getZoom(), 17.2), padding: viewPadding() });
      break;
    }
    case 'alayer': analysis.layerNo = +t.dataset.layer; analysis.focus = null; renderBody(); drawPieces(); break;
    case 'acat': case 'aitem': {
      const type = t.dataset.act === 'acat' ? 'cat' : 'item';
      const value = type === 'cat' ? t.dataset.cat : t.dataset.id;
      const same = analysis.focus && analysis.focus.type === type && analysis.focus.value === value;
      analysis.focus = same ? null : { type, value };
      drawPieces();
      if (!same) {
        const layer = analysis.result.layers.find((l) => l.layerNo === analysis.layerNo);
        const its = layer.items.filter((it) => (type === 'cat' ? it.category === value : it.id === value));
        const coords = its.flatMap((it) => it.pieces.flat(2));
        if (coords.length) pm.fitBBox(coordsBBox(coords), { padding: viewPadding(), maxZoom: 19 });
      } else pm.fitBBox(analysis.result.parcel.bbox, { padding: viewPadding() });
      break;
    }
    case 'acopy': copyText(analysisTable().map((r) => r.join('\t')).join('\n'), 'bảng kết quả'); break;
    case 'acsv': {
      const csv = '\ufeff' + analysisTable().map((r) => r.map((c) => /[",;\n]/.test(String(c)) ? `"${String(c).replace(/"/g, '""')}"` : c).join(',')).join('\r\n');
      downloadText(`phan-tich-khu-dat-${new Date().toISOString().slice(0, 10)}.csv`, csv, 'text/csv;charset=utf-8');
      break;
    }
    case 'aedit': bus.emit('tool', { name: 'parcel', mode: 'edit', ring: analysis.ring, zone: analysis.zone }); break;
    case 'aclear': clearAnalysis(); bus.emit('parcel-cleared'); break;
  }
}

/** Hovering an analysis row dims other pieces on the map (desktop only). */
function onHoverSnap(e) {
  const s = sel;
  const items = s?.nearest?.items;
  if (!items?.length || s.tab !== 'plan') return;
  const row = e.target.closest?.('.snap-row');
  if (!row) return;
  if (e.type === 'pointerout') {
    if (row.contains(e.relatedTarget)) return;
    applyHighlight();
    return;
  }
  const it = items[+row.dataset.i];
  if (it?.geometry) pm.highlight(it.geometry, s.projectGeom || null);
}

function onHoverRow(e) {
  if (!analysis || sel?.tab !== 'parcel' || analysis.focus) return;
  const row = e.target.closest?.('.arow');
  if (e.type === 'pointerout' && row && row.contains(e.relatedTarget)) return;
  const layer = analysis.result.layers.find((l) => l.layerNo === analysis.layerNo);
  if (!layer) return;
  const fc = pm.data.pieces;
  if (!fc?.features?.length) return;
  let changed = false;
  for (const f of fc.features) {
    const dim = e.type === 'pointerover' && row ? (row.dataset.act === 'acat' ? f.properties.category !== row.dataset.cat : f.properties.id !== row.dataset.id) : false;
    if (f.properties.dim !== dim) { f.properties.dim = dim; changed = true; }
  }
  if (changed) pm.setData('pieces', fc);
}

function summaryText() {
  const s = sel;
  const v = vnOf(s);
  const lines = [];
  if (s.address) lines.push(`Vị trí: ${s.address.title}${s.address.sub ? ', ' + s.address.sub : ''}`);
  lines.push(`Toạ độ WGS84: ${s.lat.toFixed(6)}, ${s.lng.toFixed(6)}`);
  lines.push(`VN-2000 (KTT 105°00′, múi 3°): X = ${formatVN(v.x, 3)}; Y = ${formatVN(v.y, 3)}`);
  for (const l of s.result?.levels || []) {
    const c = l.cells[0];
    const head = `${LEVEL_META[l.key].title}${l.project?.code ? ' (' + l.project.code + ')' : ''}`;
    if (!c && !l.zoning?.length) { lines.push(`${head}: không có dữ liệu tại vị trí`); continue; }
    const bits = [];
    if (c) {
      bits.push((c.function || c.category) + (c.code ? ` – ô ${c.code}` : ''));
      for (const [k, val2] of attrRows(c, { withArea: false })) if (!/Mã|Trạng thái/.test(k)) bits.push(`${k}: ${val2}`);
    }
    for (const z of l.zoning || []) bits.push(`Phân vùng: ${z.category || z.name}`);
    lines.push(`${head}: ${bits.join('; ')}`);
    if (l.project?.decision) lines.push(`  Quyết định: ${l.project.decision}${l.project.decisionDate ? ' ngày ' + l.project.decisionDate : ''}`);
  }
  if (s.qh100?.hit) lines.push(`QH tổng thể tầm nhìn 100 năm: ${s.qh100.hit.category || s.qh100.hit.name}`);
  lines.push('Nguồn: Hệ thống thông tin quy hoạch TP Hà Nội – thông tin tham khảo.');
  lines.push(shareUrl());
  return lines.join('\n');
}

/** A label point guaranteed inside the parcel (centroid, or the widest scan-line midpoint for concave shapes). */
function interiorOf(ring) {
  const closed = [...ring, ring[0]];
  return interiorPoint([closed]);
}

/* ---------------- reverse geocoding ---------------- */

async function reverseGeocode(lat, lng, signal) {
  try {
    const j = await getJSON(`${ESRI_GEOCODE}/reverseGeocode?f=json&langCode=vi&location=${lng.toFixed(6)},${lat.toFixed(6)}`, { signal, track: false, timeout: 7000 });
    const a = j?.address;
    if (a) {
      const d = Math.hypot((a.X - lng) * 103900, (a.Y - lat) * 110600);
      const title = (a.Addr_type === 'PointAddress' || a.Addr_type === 'StreetAddress') && d < 40 ? a.Address : a.PlaceName && d < 60 ? a.PlaceName : a.Address || a.ShortLabel || a.Match_addr;
      const sub = [a.Neighborhood, a.District, a.City, a.Region].filter((x, i, arr) => x && arr.indexOf(x) === i && x !== title).join(', ');
      if (title) return { title: d > 120 ? `Gần ${title}` : title, sub, source: 'esri' };
    }
  } catch (e) { if (isAbort(e)) return null; }
  try {
    const j = await getJSON(`${PHOTON}/reverse?lat=${lat.toFixed(6)}&lon=${lng.toFixed(6)}&limit=1&lang=default`, { signal, track: false, timeout: 7000 });
    const p = j?.features?.[0]?.properties;
    if (p) {
      const title = p.name || [p.housenumber, p.street].filter(Boolean).join(' ') || p.street || p.district;
      return { title: title || 'Vị trí đã chọn', sub: [p.street !== title ? p.street : null, p.district, p.city].filter(Boolean).join(', '), source: 'photon' };
    }
  } catch { /* ignore */ }
  return { title: 'Vị trí đã chọn', sub: 'Hà Nội' };
}

/* ======================================================================
   mobile bottom sheet
   ====================================================================== */

const sheetSnaps = () => [Math.round(innerHeight * 0.3), Math.round(innerHeight * 0.55), Math.round(innerHeight - 76)];
let sheetH = 0;
function setSheet(h, animate = true) {
  sheetH = h;
  els.panel.classList.toggle('dragging', !animate);
  els.panel.style.setProperty('--sheet-h', h + 'px');
  if (!animate) requestAnimationFrame(() => els.panel.classList.remove('dragging'));
}

function initSheet() {
  const grip = $('#sheet-grip');
  let startY = 0, startH = 0, lastY = 0, lastT = 0, v = 0, dragging = false;
  const down = (e) => {
    if (!isMobile()) return;
    dragging = true; startY = lastY = e.clientY; lastT = performance.now(); v = 0;
    startH = els.panel.getBoundingClientRect().height;
    els.panel.classList.add('dragging');
    e.target.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!dragging) return;
    const now = performance.now();
    v = (e.clientY - lastY) / Math.max(1, now - lastT); lastY = e.clientY; lastT = now;
    const h = Math.max(80, Math.min(innerHeight - 70, startH + (startY - e.clientY)));
    els.panel.style.setProperty('--sheet-h', h + 'px');
  };
  const up = () => {
    if (!dragging) return;
    dragging = false;
    els.panel.classList.remove('dragging');
    const h = els.panel.getBoundingClientRect().height;
    const snaps = sheetSnaps();
    if (h < snaps[0] * 0.7 || (v > 1.1 && h < snaps[1])) { closePanel(); return; }
    // project with velocity, snap to nearest
    const target = h - v * 180;
    const best = snaps.reduce((b, sv) => (Math.abs(sv - target) < Math.abs(b - target) ? sv : b), snaps[0]);
    setSheet(best, true);
    setTimeout(() => bus.emit('sheet', best), 380);
  };
  grip.addEventListener('pointerdown', down);
  grip.addEventListener('pointermove', move);
  grip.addEventListener('pointerup', up);
  grip.addEventListener('pointercancel', up);
  // tap on grip toggles between half and full
  grip.addEventListener('click', () => {
    if (!isMobile()) return;
    const snaps = sheetSnaps();
    setSheet(sheetH >= snaps[2] - 4 ? snaps[1] : snaps[2]);
  });
  addEventListener('resize', () => { if (isMobile() && !els.panel.hidden) { const s = sheetSnaps(); setSheet(s.reduce((b, x) => (Math.abs(x - sheetH) < Math.abs(b - sheetH) ? x : b), s[1]), false); } });
}
